import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, notification, theme } from 'antd';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Droppable from '../Droppable';
import i18n from 'constants/translation/i18n';
import DraggableBox from '../DraggableBox';
import DragTextBox from '../DragTextBox';
import { RootState } from 'store/configureStore';
import { IAssignedEls } from 'store/ducks/ComplienceEst/types';
import { gapsOp } from 'store/ducks/Gaps';
import { IAssignedEvidence, IUnassignedAudit } from 'store/ducks/Gaps/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import Helpers from 'utils/Helpers';
import * as Styled from './AuditDragAndDrop.styled';

const { useToken } = theme;

type TMockData = {
  [key: string]: { id: string; text: string }[];
};

const AuditDragAndDrop = ({
  paginatedData = []
}: {
  paginatedData?: { key: number; id: string; text: string }[];
}) => {
  const dispatch = useDispatch();
  const { token } = useToken();

  const {
    lang,
    assignedEls,
    assignedAuditFindingsUpdated,
    saveClicked,
    auditFindingMapping,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    assignedEls: state.gaps.assignedEls,
    assignedAuditFindingsUpdated: state.gaps.assignedAuditFindingsUpdated,
    saveClicked: state.complianceEst.saveClicked,
    auditFindingMapping: state.gaps.auditFindingMapping,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const [mockData, setMockData] = useState<TMockData>({
    sepRegReq: []
  });
  const [activeBox, setActiveBox] = useState<null | string>(null);
  const [newArray, setNewArray] = useState<{ id: string; text: string }[]>([]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const filterParams = Helpers.toApiFilters(orgContext);

  const getStoredFilterParams = () => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    return Object.keys(filterParams).length ? filterParams : null;
  };

  const getAudits = async () => {
    try {
      const storedFilters = getStoredFilterParams();

      if (!storedFilters) {
        notification.error({
          message: 'Filters Required',
          description: 'Please fill in the filters for displaying',
          duration: 10
        });
        return;
      }
      const { module, moduleId, ...filtersWithoutModule } = storedFilters;

      const auditFindings = await gapsOp.fetchAudits(
        lang,
        filtersWithoutModule
      );
      if (auditFindings && Array.isArray(auditFindings)) {
        const formattedAudits = auditFindings.map(
          (audit: IUnassignedAudit) => ({
            key: audit.afg_id,
            id: audit.afg_id.toString(),
            text: audit.afg_desc_en || `Audit Finding ${audit.afg_id}`
          })
        );

        setMockData({
          sepRegReq: formattedAudits
        });
      } else {
        setMockData({ sepRegReq: [] });
      }
    } catch (error) {
      setMockData({ sepRegReq: [] });
    }
  };

  useEffect(() => {
    if (paginatedData && paginatedData.length > 0) {
      setMockData({
        sepRegReq: paginatedData
      });
    } else {
      getAudits();
    }
  }, [paginatedData]);

  const handleMoveToAlignedAuditFindings = async () => {
    // Create a copy of current assignedEls before clearing
    const itemsToProcess = [...assignedEls];

    // Clear immediately to prevent duplicates on next operation
    dispatch(gapsOp.setAssignedEls([]));

    let assignedFindings: IAssignedEls[] = [];

    if (itemsToProcess && itemsToProcess.length > 0) {
      itemsToProcess.forEach(
        (item: IAssignedEvidence[] | IAssignedEvidence) => {
          const itemData = Array.isArray(item) ? item[0] : item;

          if (itemData && itemData.id && itemData.txt) {
            // Find the audit finding that matches the text
            const auditItem = mockData.sepRegReq.find(
              (audit) => audit.text === itemData.id.toString()
            );

            if (auditItem) {
              // Use the stored target requirement ID
              const targetRequirementId =
                itemData.targetId || parseInt(itemData.txt);

              assignedFindings.push({
                afg_id: parseInt(auditItem.id), // The actual audit finding ID (afg_id)
                bru_id: targetRequirementId // The correct requirement ID it's being assigned to
              });
            }
          }
        }
      );
    }

    // Remove duplicates
    const uniqueAssignments = assignedFindings.reduce(
      (acc: IAssignedEls[], current) => {
        const existingIndex = acc.findIndex(
          (item) => item.afg_id === current.afg_id
        );
        if (existingIndex === -1) {
          acc.push(current);
        }
        return acc;
      },
      []
    );

    if (uniqueAssignments.length > 0) {
      try {
        await gapsOp.fetchAuditsSave(uniqueAssignments);

        // Manually update the local state to reflect the assignment
        const currentMapping = JSON.parse(JSON.stringify(auditFindingMapping)); // Deep copy

        uniqueAssignments.forEach((assignment) => {
          const targetItem = currentMapping.find(
            (item: {
              id: number;
              AUDITS?: { afg_id: number; afg_desc: string }[];
            }) => item.id === assignment.bru_id
          );

          if (targetItem) {
            if (!targetItem.AUDITS) targetItem.AUDITS = [];
            const auditDesc =
              mockData.sepRegReq.find(
                (audit) => audit.id === assignment.afg_id.toString()
              )?.text || 'Assigned Audit Finding';
            targetItem.AUDITS.push({
              afg_id: assignment.afg_id,
              afg_desc: auditDesc
            });
          }
        });

        // Update Redux state immediately
        dispatch(gapsOp.setAuditFindingsMapping(currentMapping));

        // Remove assigned items from unassigned list and refresh from API
        setTimeout(() => {
          getAudits(); // Refresh the unassigned audit findings list
        }, 1000);
      } catch (error) {
        // Handle error silently
      }
    }

    dispatch(gapsOp.setClickedHandleMoveButton(true));
  };

  useEffect(() => {
    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
  }, []);

  useEffect(() => {
    if (!assignedAuditFindingsUpdated || paginatedData.length > 0) return;

    const filters = getStoredFilterParams();
    if (!filters || !Object.keys(filters).length) return;

    gapsOp.fetchAuditFindingsMapping(lang, filters).then(() => getAudits());
  }, [assignedAuditFindingsUpdated, lang, paginatedData]);

  useEffect(() => {
    if (
      (auditFindingMapping && auditFindingMapping.length > 0) ||
      paginatedData.length > 0
    ) {
      if (paginatedData.length === 0) {
        getAudits();
      }
    }
  }, [auditFindingMapping, paginatedData]);

  useEffect(() => {
    if (assignedEls.length && saveClicked) {
      handleMoveToAlignedAuditFindings();
      dispatch(complianceEstOp.setSaveClicked(false));
      dispatch(gapsOp.setAssignedEls([]));
    }
  }, [saveClicked, assignedEls]);

  const moveBetweenContainers = (
    colData: TMockData,
    activeContainer: string,
    activeIndex: number,
    overContainer: string,
    overIndex: number,
    item: { id: string; text: string }
  ) => ({
    ...colData,
    [activeContainer]: Helpers.removeAtIndex(
      colData[activeContainer],
      activeIndex
    ),
    [overContainer]: Helpers.insertAtIndex(
      colData[overContainer],
      overIndex,
      item
    )
  });

  const handleDragStart = (event: DragStartEvent) => {
    if (event.active.data.current) {
      setActiveBox(
        mockData[event.active.data.current.keyName][
          event.active.data.current.index
        ].text
      );
      const selectedElements = [];
      selectedElements.push(
        mockData[event.active.data.current.keyName][
          event.active.data.current.index
        ]
      );
    } else {
      setActiveBox(null);
    }
  };

  const handleDragCancel = () => setActiveBox(null);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.data.current && over.data.current) {
      const activeContainer = active.data.current.sortable.containerId;
      const overContainer = over.data.current.sortable.containerId;
      const overIndex =
        over.id in mockData
          ? mockData[overContainer].length + 1
          : over.data.current.sortable.index;
      const activeIndex = active.data.current.index;
      let newItems;
      if (activeContainer === overContainer) {
        newItems = {
          ...mockData,
          [overContainer]: arrayMove(
            mockData[overContainer],
            activeIndex,
            overIndex
          )
        };
      } else {
        const itemToMove = mockData[activeContainer][activeIndex];

        // Get the target requirement ID from the drop zone
        const targetRequirementId = over.data.current?.targetId || over.id;

        // Store the target requirement ID with the moved item
        const itemWithTarget = {
          ...itemToMove,
          targetRequirementId: targetRequirementId
        };

        newItems = moveBetweenContainers(
          mockData,
          activeContainer,
          activeIndex,
          overContainer,
          overIndex,
          itemWithTarget
        );
        setNewArray([...newArray, itemWithTarget]);
        setMockData({
          ...newItems,
          [activeContainer]: Helpers.removeAtIndex(
            mockData[activeContainer],
            activeIndex
          )
        });

        // Update assignedEls with the correct target requirement ID
        const newAssignedItem: IAssignedEvidence = {
          id: itemToMove.id,
          txt: targetRequirementId.toString(),
          targetId: targetRequirementId
        };
        // Only add if not already in the list
        const isDuplicate = assignedEls.some(
          (item: IAssignedEvidence | IAssignedEvidence[]) => {
            const itemData = Array.isArray(item) ? item[0] : item;
            return itemData?.id === newAssignedItem.id;
          }
        );
        if (!isDuplicate) {
          dispatch(gapsOp.setAssignedEls(newAssignedItem));
        }
      }
      setMockData(newItems);
      setActiveBox(null);
    }
  };

  const renderCols = () => {
    const colNum = Object.keys(mockData).length;
    return Object.keys(mockData)?.map((key: string, index: number) => (
      <Droppable
        colNum={colNum}
        keyName={key}
        arrayData={mockData.sepRegReq}
        key={index}
      />
    ));
  };

  const hasData = mockData?.sepRegReq?.length > 0;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <Row className='justify-content-end m-3'>
        <Styled.StyledButton
          type='primary'
          token={token}
          onClick={handleMoveToAlignedAuditFindings}
          disabled={!hasData}
        >
          {i18n.t('MOVE')}
        </Styled.StyledButton>
      </Row>
      <Styled.StyledRow className='drag-overlay'>
        {renderCols()}
      </Styled.StyledRow>
      <DragOverlay>
        {activeBox ? (
          <DraggableBox isOverlay={true}>
            <DragTextBox>{activeBox}</DragTextBox>
          </DraggableBox>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default AuditDragAndDrop;
